import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { Hourglass, Timer, Sprout, Leaf } from "lucide-react";
import { Section, Card, Kpi, GenderLegend, Empty, GIcon, C, axis, tip } from "./parts";
import { fmtTenure } from "@/lib/workforceStats";

const GenderTenure = ({ rows }) => {
    const max = Math.max(1, ...rows.map((r) => r.avg || 0));
    return (
        <div className="space-y-4">
            {rows.map((r) => (
                <div key={r.gender} data-testid={`tenure-gender-${r.gender}`}>
                    <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 font-medium text-slate-800 capitalize"><GIcon g={r.gender} className="h-3.5 w-3.5" />{r.gender} <span className="text-xs text-slate-400 font-normal">({r.count})</span></span>
                        <span className="text-xs text-slate-600 tabular-nums">avg <b>{fmtTenure(r.avg)}</b> · median <b>{fmtTenure(r.median)}</b></span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${((r.avg || 0) * 100) / max}%`, background: C[r.gender] }} />
                    </div>
                </div>
            ))}
        </div>
    );
};

export const TenureSection = ({ t }) => {
    const b = Object.fromEntries(t.bands.map((x) => [x.label, x.total]));
    const tiles = [["Under 6m", "Under 6 months"], ["6–12m", "6–12 months"], ["1–2y", "1–2 years"], ["2–5y", "2–5 years"], ["5+y", "5+ years"]];
    return (
        <Section no="02" title="Tenure Analytics" sub={`based on ${t.count} people with a joining date`} testid="wf-tenure">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Average tenure" value={fmtTenure(t.avg)} sub={t.avg == null ? null : `${Math.round(t.avg)} months · ${(t.avg / 12).toFixed(1)} years`} icon={<Hourglass className="h-3 w-3" />} testid="tenure-avg" />
                <Kpi label="Median tenure" value={fmtTenure(t.median)} sub={t.median == null ? null : `${Math.round(t.median)} months`} icon={<Timer className="h-3 w-3" />} testid="tenure-median" />
                <Kpi label="Under 6 months" value={b["Under 6m"] ?? 0} tone="text-amber-700" icon={<Sprout className="h-3 w-3" />} sub="New to the company" testid="tenure-under6" />
                <Kpi label="6–12 months" value={b["6–12m"] ?? 0} icon={<Leaf className="h-3 w-3" />} sub="Finishing their first year" testid="tenure-6to12" />
            </div>
            <div className="grid lg:grid-cols-3 gap-3">
                <Card title="Service length bands" className="lg:col-span-2" testid="tenure-bands">
                    {t.count ? (
                        <>
                            <div className="grid grid-cols-5 gap-2 mb-3">
                                {tiles.map(([k, l]) => (
                                    <div key={k} className="rounded-md bg-slate-50 px-2 py-1.5 text-center" data-testid={`tenure-band-${k}`}>
                                        <div className="text-lg font-bold text-slate-900 tabular-nums">{b[k]}</div>
                                        <div className="text-[10px] text-slate-500">{l}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                                    <BarChart maxBarSize={48} data={t.bands} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                                        <XAxis dataKey="label" {...axis} />
                                        <YAxis allowDecimals={false} {...axis} />
                                        <Tooltip {...tip} />
                                        <Bar dataKey="male" name="Male" stackId="g" fill={C.male} />
                                        <Bar dataKey="female" name="Female" stackId="g" fill={C.female} />
                                        <Bar dataKey="unset" name="Not set" stackId="g" fill={C.unset} radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <GenderLegend />
                        </>
                    ) : <Empty>No joining dates recorded yet.</Empty>}
                </Card>
                <Card title="Gender-wise tenure comparison" testid="tenure-gender">
                    {t.byGender.some((r) => r.count) ? <GenderTenure rows={t.byGender} /> : <Empty>Set gender and joining dates to compare.</Empty>}
                </Card>
            </div>
        </Section>
    );
};
